import GIS from "./app";
import GeoJSON from "ol/format/GeoJSON";
import {Feature} from "ol";
import {Geometry, Polygon as OlPolygon} from "ol/geom";
import {getIdProperty} from "./function/layer";
import {
    findAreaGeometryIssues,
    findRingSpikes,
    MeasureRing,
    removeGeometrySpikes,
    sanitizeAreaGeometry
} from "./function/topology";
import {
    GeometryIssue,
    GeometryRepairResult,
    GeometryValidationResult,
    RemoveSpikesOptions,
    SliverThresholds,
    SpikeRemovalResult,
    SpikeThresholds,
    ValidateGeometryOptions
} from "./configuration/topology";
import {TurfFeatureGeneric} from "./configuration/analysis";
import {GetGeometryRepair, GetGeometryValidation, GetSpikeRemoval} from "./general/callback";

/**
 * Topology repairs for features already on a layer.
 *
 * Geometric operations and hand editing can leave a polygon topologically broken
 * while its area still looks right — spikes, duplicate vertices, needle slivers.
 * This module collects the repairs for those defects behind one consistent,
 * layer-and-id API, the same shape the other modules use:
 *
 * ```ts
 * GIS.Topology.removeSpikes('forest_compartment', 11599031);
 * ```
 *
 * The geometry maths lives in `src/function/topology` as pure, projection-agnostic
 * functions, so a repair can also be reused internally without touching a layer —
 * {@link Analysis.splitPolygon} cleans the parts it produces that way.
 */
class Topology {
    private readonly GIS: GIS;

    /**
     * Shared GeoJSON format instance, reused to avoid per-call allocations.
     */
    private readonly geoJSONFormat = new GeoJSON();

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    /**
     * Reads a layer feature, throwing the same errors as the sibling modules when it
     * cannot be resolved.
     * @throws No such layer in the map.
     * @throws Could not find feature.
     */
    private getFeature(layerName: string, id: string | number, idProperty: string): Feature {
        const olFeature = this.GIS.Layer.getOlFeatureByAttributeID(layerName, idProperty, id);

        if (!olFeature) {
            throw new Error('Could not find feature.');
        }

        return olFeature;
    }

    /**
     * Real-world measurement of one linear ring — square meters and meters — the
     * {@link MeasureRing} the pure sliver tests need. Geodesic, so a width verdict
     * means the same thing in every projection.
     */
    private readonly measureRing: MeasureRing = (ring: number[][]) => {
        const geometry = new OlPolygon([ring]);
        return {
            area: this.GIS.Projection.measure(geometry, 'area'),
            perimeter: this.GIS.Projection.measure(geometry, 'perimeter')
        };
    };

    /**
     * One line summarizing a set of {@link GeometryIssue}s — the message a caller can
     * put straight in front of the user when a purchase area fails validation.
     * Defects are counted per kind so a boundary littered with a hundred needle
     * vertices reports "100 spikes" rather than a hundred separate lines.
     */
    private validationMessage(issues: GeometryIssue[]): string {
        if (issues.length === 0) {
            return 'Geometry is valid.';
        }

        const labels: {[type: string]: [string, string]} = {
            'empty': ['empty geometry', 'empty geometries'],
            'not-area': ['non-area geometry', 'non-area geometries'],
            'unclosed-ring': ['unclosed ring', 'unclosed rings'],
            'duplicate-vertex': ['duplicate vertex', 'duplicate vertices'],
            'degenerate-ring': ['degenerate ring', 'degenerate rings'],
            'spike': ['spike', 'spikes'],
            'self-intersection': ['self-intersection', 'self-intersections'],
            'sliver': ['sliver', 'slivers']
        };

        const counts = new Map<string, number>();
        issues.forEach((issue) => counts.set(issue.type, (counts.get(issue.type) ?? 0) + 1));

        const summary = [...counts.entries()].map(([type, count]) => {
            const [singular, plural] = labels[type] ?? [type, type];
            return `${count} ${count === 1 ? singular : plural}`;
        }).join(', ');

        return `Invalid geometry: ${summary}.`;
    }

    /**
     * Detect and remove geometry spikes from one feature of a layer, cleaning its
     * geometry in place.
     *
     * A **spike** is a needle-thin protrusion: three consecutive ring vertices
     * A-B-C where the edges B→A and B→C point in almost the same direction, so the
     * boundary runs out to the tip B and immediately doubles back on itself. Spikes
     * carry (almost) no area, but they are visible on the map, they break
     * area/border-length measurement and they make a polygon fail validity checks.
     *
     * They are a numerical by-product of {@link Analysis.splitPolygon}, which cuts
     * by subtracting the *buffered* cutting line from the polygon: when that line
     * runs along an existing polygon edge — the situation when a polygon drawn on
     * the real-estate (RE) boundary is later split along it — the buffered strip
     * grazes the boundary at a near-zero angle, and the polygon clipper emits needle
     * vertices there. Floating-point rounding decides which side of the
     * near-collinear boundary each vertex falls on, which is why the defect appears
     * only intermittently.
     *
     * Detection is driven by the vertex **interior angle**, which is dimensionless
     * and therefore independent of the layer's projection and units: artifact
     * needles measure a small fraction of a degree, while genuine polygon corners
     * never do. `options.maxArea` adds an optional size guard for the rare case of a
     * legitimate long-and-thin feature (a real peninsula) whose tip is genuinely
     * very acute.
     *
     * The feature's geometry is rewritten only when at least one spike is found, and
     * its area / border-length measurement is refreshed automatically, so a measured
     * feature's overlay reflects the cleaned shape. {@link Analysis.splitPolygon}
     * already applies this cleaning to every part it produces — call this method for
     * features that come from elsewhere (imported data, an older split, a hand-drawn
     * polygon).
     *
     * @param layerName - Layer holding the feature.
     * @param id - Id value of the feature to clean.
     * @param options - `attribute` is the id property (default `'id'`); `angle`
     * (default `1` degree) and `maxArea` (default disabled) are the
     * {@link SpikeThresholds}.
     * @param callback - Optional callback with the {@link SpikeRemovalResult}.
     * @returns `{removed, feature}` — how many spike vertices were deleted (`0` when
     * the feature was already clean) and the resulting geometry as a GeoJSON Feature
     * in the map view projection.
     * @throws No such layer in the map.
     * @throws Could not find feature.
     *
     * @example
     * // Clean a polygon that shows spikes after being split on the RE boundary
     * const {removed} = GIS.Topology.removeSpikes('forest_compartment', 11599031);
     * if (removed) console.log(`${removed} spike vertices removed`);
     *
     * @example
     * // Custom thresholds: treat anything up to 2 degrees as a spike, but keep tips
     * // whose triangle is larger than 5 m² (protects real peninsulas).
     * GIS.Topology.removeSpikes('forest_compartment', 11599031, {
     *     attribute: 'id', angle: 2, maxArea: 5
     * }, result => console.log(result.removed, result.feature));
     */
    public removeSpikes(layerName: string, id: string | number, options?: RemoveSpikesOptions, callback?: GetSpikeRemoval): SpikeRemovalResult {
        const idProperty = getIdProperty(options);
        const olFeature = this.getFeature(layerName, id, idProperty);

        const geojsonFeature = JSON.parse(this.geoJSONFormat.writeFeature(olFeature)) as TurfFeatureGeneric;
        const {removed, geometry} = removeGeometrySpikes(geojsonFeature.geometry as any, options);

        const result: SpikeRemovalResult = {
            removed,
            feature: {...geojsonFeature, geometry}
        };

        // Only touch the feature when a spike was actually found — this avoids
        // needless geometry-change events on already clean features.
        if (removed > 0) {
            const cleanedFeature = this.geoJSONFormat.readFeature(result.feature) as Feature<Geometry>;
            const cleanedGeometry = cleanedFeature.getGeometry();

            if (cleanedGeometry) {
                olFeature.setGeometry(cleanedGeometry);
                // The stored area / border-length values describe the spiked shape;
                // recompute them so a measured feature's overlay matches the geometry.
                this.GIS.Edit.recalculateMeasurement(olFeature);
            }
        }

        if (callback) {
            callback(result);
        }

        return result;
    }

    /**
     * Report the spikes of one feature of a layer **without changing it** — the
     * read-only counterpart of {@link removeSpikes}, for highlighting the defect or
     * deciding whether a repair is needed.
     *
     * @param layerName - Layer holding the feature.
     * @param id - Id value of the feature to inspect.
     * @param options - `attribute` is the id property (default `'id'`); `angle` and
     * `maxArea` are the {@link SpikeThresholds}.
     * @returns The spike vertex coordinates, in ring order. Empty when the feature is
     * clean or is not an area geometry.
     * @throws No such layer in the map.
     * @throws Could not find feature.
     *
     * @example
     * const spikes = GIS.Topology.findSpikes('forest_compartment', 11599031);
     * if (spikes.length) console.log('spike tips at', spikes);
     */
    public findSpikes(layerName: string, id: string | number, options?: RemoveSpikesOptions): number[][] {
        const idProperty = getIdProperty(options);
        const olFeature = this.getFeature(layerName, id, idProperty);

        const geometry = JSON.parse(this.geoJSONFormat.writeFeature(olFeature)).geometry;
        if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
            return [];
        }

        const thresholds: SpikeThresholds = {angle: options?.angle, maxArea: options?.maxArea};
        const rings: number[][][] = geometry.type === 'Polygon'
            ? geometry.coordinates
            : geometry.coordinates.flat();

        return rings.flatMap((ring) =>
            findRingSpikes(ring, thresholds).map((vertexIndex) => ring[vertexIndex])
        );
    }

    /**
     * Validate the geometry of one feature **without changing it**, and get back a
     * plain-language reason when it is not fit to be saved.
     *
     * This is the gate to put in front of persisting a drawn or clipped polygon — a
     * purchase area in particular. A polygon can come out of the "create new area"
     * workflow looking right on screen while being topologically broken, because
     * every geometric operation on it runs through a polygon clipper that emits
     * artifacts wherever two input boundaries are nearly coincident (the normal
     * case when a new polygon is snapped onto an existing one). Saving such a
     * geometry pushes the defect downstream, where the area total is already wrong
     * and the shape can no longer be repaired.
     *
     * Every kind of defect is reported — see {@link GeometryIssueType}:
     * needle **spikes**, **sliver** parts and holes, **degenerate rings**,
     * **duplicate vertices**, **unclosed rings**, **self-intersections**, and a
     * geometry that is missing or is not an area type at all.
     *
     * A non-area geometry (Point, LineString, …) is reported as `not-area` rather
     * than quietly passing, so calling this on the wrong feature is visible instead
     * of looking like a clean bill of health.
     *
     * @param layerName - Layer holding the feature.
     * @param id - Id value of the feature to validate.
     * @param options - `attribute` is the id property (default `'id'`); `angle` /
     * `maxArea` are the {@link SpikeThresholds}; `minArea` / `maxWidth` the
     * {@link SliverThresholds}; `tolerance` the self-intersection contact tolerance
     * in map units (default `0`, exact contacts only).
     * @param callback - Optional callback with the {@link GeometryValidationResult}.
     * @returns `{valid, issues, message}` — `valid` is `true` only when `issues` is
     * empty; `message` is one line ready to be surfaced to the user.
     * @throws No such layer in the map.
     * @throws Could not find feature.
     *
     * @example
     * // Refuse to save a purchase area whose geometry is broken, and say why.
     * const {valid, message} = GIS.Topology.validateGeometry('purchase_area', 42);
     * if (!valid) {
     *     showError(message); // e.g. "Invalid geometry: 1 sliver, 2 spikes."
     *     return;
     * }
     * save();
     *
     * @example
     * // Inspect the individual defects, e.g. to zoom to the first one.
     * const {issues} = GIS.Topology.validateGeometry('purchase_area', 42);
     * issues.forEach((issue) => console.log(issue.type, issue.coordinate, issue.message));
     */
    public validateGeometry(layerName: string, id: string | number, options?: ValidateGeometryOptions, callback?: GetGeometryValidation): GeometryValidationResult {
        const idProperty = getIdProperty(options);
        const olFeature = this.getFeature(layerName, id, idProperty);

        const result = this.validateFeature(olFeature, options);

        if (callback) {
            callback(result);
        }

        return result;
    }

    /**
     * The same validation as {@link validateGeometry}, on a feature that is already
     * in hand rather than one looked up by layer and id.
     *
     * This is what makes validation possible at `drawend`, the moment a sketch turns
     * into a real feature: the feature exists but is not on the layer's source yet,
     * so there is nothing to look up. {@link Edit.drawFeatures} calls this when its
     * `validate` option is on. It mirrors {@link Edit.recalculateMeasurement}, which
     * takes a live feature for the same reason.
     *
     * @param olFeature - The OpenLayers feature to validate. Not modified.
     * @param options - The {@link SpikeThresholds} / {@link SliverThresholds} and the
     * self-intersection `tolerance`; unlike {@link validateGeometry} no id property is
     * needed.
     * @returns `{valid, issues, message}`.
     */
    public validateFeature(olFeature: Feature, options?: SpikeThresholds & SliverThresholds & {tolerance?: number}): GeometryValidationResult {
        const geometry = olFeature && olFeature.getGeometry()
            ? JSON.parse(this.geoJSONFormat.writeFeature(olFeature)).geometry
            : null;

        const issues = findAreaGeometryIssues(geometry, this.measureRing, options);

        return {
            valid: issues.length === 0,
            issues,
            message: this.validationMessage(issues)
        };
    }

    /**
     * Repair the geometry of one feature in place, removing every defect that *can*
     * be removed, and report what was wrong.
     *
     * The companion to {@link validateGeometry}: validate before saving, and offer
     * this when the answer is "no". It applies the same cleaning
     * ({@link sanitizeAreaGeometry}) that every clip result inside
     * {@link Analysis} already goes through — spikes, duplicate vertices,
     * degenerate rings, sliver holes and sliver parts — so a polygon that came from
     * elsewhere (imported data, an older release, a hand edit) can be brought up to
     * the same standard.
     *
     * What it does **not** do is invent geometry. Two defects are therefore reported
     * but not fixed, and leave the feature untouched with `valid: false`:
     *
     * - a genuine **self-intersection** — the user has to say which way the boundary
     *   was meant to run,
     * - a feature that is a **sliver as a whole** — the repair would be to delete
     *   the feature, which is the application's decision, not a geometry fix.
     *
     * As with {@link removeSpikes}, the geometry is rewritten only when something is
     * actually removed, and the area / border-length measurement is refreshed so a
     * measured feature's overlay matches the repaired shape.
     *
     * @param layerName - Layer holding the feature.
     * @param id - Id value of the feature to repair.
     * @param options - Same as {@link validateGeometry}.
     * @param callback - Optional callback with the {@link GeometryRepairResult}.
     * @returns `{removed, feature, issues, valid, message}` — how many vertices the
     * repair deleted (`0` when the feature was already clean), the resulting GeoJSON
     * Feature in the view projection, the defects that were present, and whether the
     * result is now valid.
     * @throws No such layer in the map.
     * @throws Could not find feature.
     *
     * @example
     * const {removed, valid, message} = GIS.Topology.repairGeometry('purchase_area', 42);
     * if (!valid) showError(message);   // a defect no repair can decide on
     * else if (removed) console.log(`${removed} artifact vertices removed`);
     */
    public repairGeometry(layerName: string, id: string | number, options?: ValidateGeometryOptions, callback?: GetGeometryRepair): GeometryRepairResult {
        const idProperty = getIdProperty(options);
        const olFeature = this.getFeature(layerName, id, idProperty);

        const geojsonFeature = JSON.parse(this.geoJSONFormat.writeFeature(olFeature)) as TurfFeatureGeneric;
        const issues = findAreaGeometryIssues(geojsonFeature.geometry as any, this.measureRing, options);

        const sliverThresholds = {minArea: options?.minArea, maxWidth: options?.maxWidth};
        const {geometry, removedVertices} = sanitizeAreaGeometry(
            geojsonFeature.geometry as {type: string, coordinates: any},
            this.measureRing,
            {angle: options?.angle, maxArea: options?.maxArea, slivers: sliverThresholds}
        );

        // Nothing survived the cleaning: the feature IS an artifact. Removing it is
        // the application's call, so the geometry is left exactly as it was and the
        // caller gets an unambiguous `valid: false` with the reason.
        if (!geometry) {
            const result: GeometryRepairResult = {
                removed: 0,
                feature: geojsonFeature,
                issues,
                valid: false,
                message: 'Geometry cannot be repaired: nothing but slivers and degenerate rings left.'
            };

            if (callback) {
                callback(result);
            }

            return result;
        }

        const repairedFeature = {...geojsonFeature, geometry} as TurfFeatureGeneric;

        if (removedVertices > 0) {
            const cleanedFeature = this.geoJSONFormat.readFeature(repairedFeature) as Feature<Geometry>;
            const cleanedGeometry = cleanedFeature.getGeometry();

            if (cleanedGeometry) {
                olFeature.setGeometry(cleanedGeometry);
                // The stored area / border-length values describe the broken shape;
                // recompute them so a measured feature's overlay matches the repair.
                this.GIS.Edit.recalculateMeasurement(olFeature);
            }
        }

        const remaining = findAreaGeometryIssues(repairedFeature.geometry as any, this.measureRing, options);

        const result: GeometryRepairResult = {
            removed: removedVertices,
            feature: repairedFeature,
            issues,
            valid: remaining.length === 0,
            message: remaining.length > 0
                ? this.validationMessage(remaining)
                : removedVertices > 0
                    ? `Geometry repaired: ${removedVertices} artifact ${removedVertices === 1 ? 'vertex' : 'vertices'} removed.`
                    : 'Geometry is valid.'
        };

        if (callback) {
            callback(result);
        }

        return result;
    }
}

export default Topology;
